import { useEffect, useState } from "react";

import type { ColumnDef } from "@tanstack/react-table";

import { ArrowUpDown, Info } from "lucide-react";
import type { DateRange } from "react-day-picker";

import { DatePickerWithRange } from "@/shared/ui/date-picker-range";
import { type Lead, LeadStatus } from "@/entities/lead-v2";
import { localizationMap } from "@/shared/lib/consts";
import { formatDate, numberWithSpaces } from "@/shared/lib/utils";
import { DataTableCenteredCell } from "@/shared/ui/DataTable";
import { HoverCardInfo } from "@/features/HoverCardInfo";
import { useURLParamsData } from "@/shared/hooks/useURLParamsData.ts";
import { StatusModal } from "@/widgets/StatusModal";
import { ProfitHoverCard } from "@/features/WorkerLeadProfitHoverCard";
import { InfoTooltip } from "@/features/InfoTooltip/InfoTooltip";
import { SortButton } from "@/features/SortButton/SortButton";
import { Modal } from "@/shared/ui/Modal";
import { Label } from "@/shared/ui/label";
import { FilterPopup } from "@/widgets/FilterPopup";
import { useLeadLinksInf } from "@/entities/lead-link";
import { useFlattenedInfinityData } from "@/shared/hooks";
import { useSessionStore } from "@/entities/session";

export const LeadsColumns: ColumnDef<Lead>[] = [
  {
    id: "telegramId",
    accessorKey: "telegramId",
    header: "ID",
  },
  {
    id: "telegramNickname",
    accessorKey: "telegramNickname",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        Имя
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.telegramNickname || "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    id: "telegramUsername",
    accessorKey: "telegramUsername",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        TG Username
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.telegramUsername || "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    id: "profit",
    accessorKey: "profit",
    enableSorting: true,
    header: () => (
      <DataTableCenteredCell>
        <SortButton sort="profit" title="Прибыль" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => {
      const { id, profit } = row.original;
      const formattedEarnings =
        typeof profit === "number" ? numberWithSpaces(profit) : "N/A";

      return (
        <DataTableCenteredCell>
          <ProfitHoverCard formattedEarnings={formattedEarnings} leadId={id} />
        </DataTableCenteredCell>
      );
    },
  },
  {
    id: "status",
    accessorKey: "status",
    header: () => <StatusModal />,
    cell: function Cell({ row }) {
      const { irrelevance, rejectionReason } = row.original;
      const { leadStatus } = useURLParamsData();

      const warn = irrelevance.value && leadStatus !== LeadStatus.DEPOSIT;

      return (
        <div
          className={`${warn ? "text-orange-500" : ""} flex items-center justify-center gap-x-2`}
        >
          <div className="flex items-center justify-start gap-0.5">
            <HoverCardInfo
              text={
                localizationMap[row.original.status.value.toLowerCase()] ||
                row.original.status.value
              }
              className="text-nowrap"
            >
              {localizationMap[row.original.status.value.toLowerCase()] ||
                row.original.status.value}
            </HoverCardInfo>
            {rejectionReason && (
              <Modal>
                <Modal.Trigger asChild>
                  <button className="transition-colors hover:text-orange-500">
                    <Info className={"h-4 w-4"} />
                  </button>
                </Modal.Trigger>
                <Modal.Content>
                  <Modal.Title>Причина отклонения</Modal.Title>
                  <Label>{rejectionReason}</Label>
                  <Modal.Close>Понятно</Modal.Close>
                </Modal.Content>
              </Modal>
            )}
          </div>
          {warn ? (
            <InfoTooltip warning={true}>
              <p>Статус неактуальный, необходимо обновить статус</p>
            </InfoTooltip>
          ) : null}
        </div>
      );
    },
  },
  {
    id: "createdAt",
    accessorKey: "createdAt",
    header: function Cell() {
      const [date, setDate] = useState<DateRange | undefined>();
      const { setCreatedAtFrom, setCreatedAtTo } = useURLParamsData();

      useEffect(() => {
        if (date?.from) {
          setCreatedAtFrom(date.from);
        }

        if (date?.to) {
          setCreatedAtTo(date.to);
        }
      }, [date]);

      return (
        <DataTableCenteredCell>
          <DatePickerWithRange date={date} setDate={setDate}>
            <button className="flex items-center gap-2 text-thColor">
              Зашел <ArrowUpDown size={14} />
            </button>
          </DatePickerWithRange>
        </DataTableCenteredCell>
      );
    },
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.createdAt ? formatDate(row.original.createdAt) : "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    id: "lastChange",
    accessorKey: "lastChange",
    header: () => (
      <DataTableCenteredCell>
        <SortButton sort="updatedAt" title="Изменён" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => {
      const { status } = row.original;

      return (
        <DataTableCenteredCell className="flex-row gap-2">
          {row.original.updatedAt ? formatDate(row.original.updatedAt) : "N/A"}

          {status.history && (
            <InfoTooltip>
              <ul>
                {status.history.map((s, idx) => (
                  <li key={idx}>
                    {localizationMap[s.value.toLowerCase()]} -{" "}
                    {s.createdAt && formatDate(s.createdAt)}
                  </li>
                ))}
              </ul>
            </InfoTooltip>
          )}
        </DataTableCenteredCell>
      );
    },
  },
  {
    id: "ref.name",
    accessorKey: "ref.name",
    header: function Header() {
      const { user } = useSessionStore();
      const [search, setSearch] = useState("");
      const [open, setOpen] = useState(false);

      const linksQuery = useLeadLinksInf(
        { userId: user?._id!, search: search, pageSize: 5 },
        { enabled: open },
      );
      const modifiedData = useFlattenedInfinityData(linksQuery.data);
      return (
        <DataTableCenteredCell className="text-thColor">
          <FilterPopup
            label="Лид ссылка"
            query={linksQuery}
            modifiedData={modifiedData.data}
            extractValue={(l) => l._id}
            renderItem={(l) => <p>{l.name}</p>}
            searchParamLabel="refIds"
            setSearch={setSearch}
            open={open}
            setOpen={setOpen}
          />
        </DataTableCenteredCell>
      );
    },
    cell: ({ row }) => (
      <div className="flex max-w-64 justify-center truncate">
        {row.original.ref?.name || "N/A"}
      </div>
    ),
  },
];
